import React, { useState, useEffect, useRef } from "react";
import { Clock, Zap, ShoppingCart } from "lucide-react";
import { getCountdownSettings, getFlashDealSettings, getPromoPriority } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

/**
 * Unified promotion banner – shows only ONE promotion at a time
 * based on admin-configured priority order.
 * Default priority: flashdeal > countdown > popup
 */
export default function PromoBanner({ product, categoryPath }) {
    const [mode, setMode] = useState(null);
    const [flashCfg, setFlashCfg] = useState(null);
    const [countdownCfg, setCountdownCfg] = useState(null);
    const [timeLeft, setTimeLeft] = useState(0);
    const endTimeRef = useRef(null);
    const { addItem } = useCart();
    const navigate = useNavigate();

    useEffect(() => {
        let active = true;
        Promise.allSettled([
            getFlashDealSettings(),
            getCountdownSettings(),
            getPromoPriority(),
        ]).then(([flashRes, cdRes, prioRes]) => {
            if (!active) return;
            const flash = flashRes.status === "fulfilled" ? flashRes.value.data : null;
            const cd = cdRes.status === "fulfilled" ? cdRes.value.data : null;
            const prio = prioRes.status === "fulfilled" ? prioRes.value.data : null;

            const priorityOrder = prio?.order || ["flashdeal", "countdown", "popup"];

            // Check Flash Deal targeting
            let flashMatch = false;
            if (flash?.enabled) {
                if (flash.target_type === "all") flashMatch = true;
                else if (flash.target_type === "category" && flash.target_categories?.length > 0) {
                    const slugs = (categoryPath || []).map(c => c.slug);
                    flashMatch = flash.target_categories.some(ts => slugs.includes(ts));
                } else if (flash.target_type === "product" && flash.target_product_ids?.length > 0) {
                    flashMatch = flash.target_product_ids.includes(product?.id);
                }
            }

            // Build candidate map
            const candidates = {
                flashdeal: flashMatch ? { type: "flash", cfg: flash, duration: flash?.duration_minutes || 10 } : null,
                countdown: cd?.enabled ? { type: "countdown", cfg: cd, duration: cd?.duration_minutes || 5 } : null,
            };

            // Pick winner by priority order
            let winner = null;
            for (const key of priorityOrder) {
                if (candidates[key]) { winner = candidates[key]; break; }
            }
            if (!winner) return;

            // Session timer
            const sessionKey = `sg_promo_${winner.type}_end`;
            let stored = sessionStorage.getItem(sessionKey);
            if (stored && parseInt(stored) > Date.now()) {
                endTimeRef.current = parseInt(stored);
            } else {
                endTimeRef.current = Date.now() + winner.duration * 60 * 1000;
                sessionStorage.setItem(sessionKey, String(endTimeRef.current));
            }

            if (winner.type === "flash") setFlashCfg(winner.cfg);
            else setCountdownCfg(winner.cfg);
            setMode(winner.type);
            setTimeLeft(Math.max(0, endTimeRef.current - Date.now()));
        });
        return () => { active = false; };
    }, [product?.id, categoryPath]);

    useEffect(() => {
        if (!mode || timeLeft <= 0) return;
        const timer = setInterval(() => {
            const r = Math.max(0, endTimeRef.current - Date.now());
            setTimeLeft(r);
            if (r <= 0) clearInterval(timer);
        }, 1000);
        return () => clearInterval(timer);
    }, [mode, timeLeft]);

    if (!mode || timeLeft <= 0) return null;

    const mins = Math.floor(timeLeft / 60000);
    const secs = Math.floor((timeLeft % 60000) / 1000);

    // Flash Deal render
    if (mode === "flash" && flashCfg) {
        const basePrice = product?.price || 0;
        let dealPrice, discountLabel;
        if (flashCfg.discount_type === "fixed") {
            dealPrice = Math.max(0, basePrice - flashCfg.discount_value);
            discountLabel = `-${flashCfg.discount_value} Kc`;
        } else {
            dealPrice = Math.round(basePrice * (1 - flashCfg.discount_value / 100));
            discountLabel = `-${flashCfg.discount_value}%`;
        }

        const handleBuyNow = () => {
            if (product.stock_status === "out_of_stock") { toast.error("Produkt je vyprodan"); return; }
            addItem({ ...product, price: dealPrice, original_price: product.price, sale_price: null }, 1, dealPrice);
            toast.success(`${product.name} pridan se slevou!`);
            navigate("/pokladna");
        };

        const bg = flashCfg.bg_color || "#fef3c7";
        const fg = flashCfg.text_color || "#92400e";

        return (
            <div className="rounded-lg border-2 border-dashed p-3 mb-3" style={{ backgroundColor: bg, borderColor: fg + "40" }}
                data-testid="promo-banner-flash">
                <div className="flex items-center gap-2 mb-2">
                    <Zap size={14} style={{ color: fg }} className="flex-shrink-0" />
                    <span className="text-xs font-bold" style={{ color: fg }}>{flashCfg.message || "Omezena nabidka"}</span>
                    <div className="flex items-center gap-1 ml-auto">
                        <Clock size={12} style={{ color: fg }} />
                        <span className="font-mono font-bold text-sm tabular-nums" style={{ color: fg }}>
                            {String(mins).padStart(2, "0")}:{String(secs).padStart(2, "0")}
                        </span>
                    </div>
                    {flashCfg.show_discount_badge && (
                        <span className="text-white font-bold text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: fg }}>{discountLabel}</span>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex items-baseline gap-2">
                        <span className="text-lg font-bold" style={{ color: fg }}>{dealPrice.toLocaleString("cs-CZ")} Kc</span>
                        {flashCfg.show_original_price && basePrice !== dealPrice && (
                            <span className="text-xs line-through opacity-60" style={{ color: fg }}>{basePrice.toLocaleString("cs-CZ")} Kc</span>
                        )}
                    </div>
                    <button onClick={handleBuyNow} disabled={product?.stock_status === "out_of_stock"}
                        className="ml-auto flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-xs font-bold transition-all hover:opacity-90 disabled:opacity-50"
                        style={{ backgroundColor: fg }} data-testid="flash-deal-buy-btn">
                        <ShoppingCart size={13} /> {flashCfg.cta_text || "Koupit ihned se slevou"}
                    </button>
                </div>
            </div>
        );
    }

    // Countdown Banner render
    if (mode === "countdown" && countdownCfg) {
        return (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 mb-3"
                data-testid="promo-banner-countdown">
                <Clock size={14} className="text-red-500 flex-shrink-0" />
                <span className="text-xs font-medium text-red-700">{countdownCfg.message || "Akce konci za"}</span>
                <span className="font-mono font-bold text-sm text-red-600 tabular-nums ml-auto">
                    {String(mins).padStart(2, "0")}:{String(secs).padStart(2, "0")}
                </span>
                {(countdownCfg.discount_value || countdownCfg.discount_percent || 0) > 0 && (
                    <span className="bg-red-600 text-white font-bold text-[10px] px-1.5 py-0.5 rounded">
                        -{countdownCfg.discount_value ?? countdownCfg.discount_percent}{(countdownCfg.discount_type || "percent") === "percent" ? "%" : " Kč"}
                    </span>
                )}
            </div>
        );
    }

    return null;
}
